import { FC, useState } from "react";

import {
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";

import { Collapse } from "components/Collapse";
import { ParentNodeIcon } from "components/ParentNodeIcon";
import { generateFooterAnalyticsProp } from "utils/generateAnalyticsTag";
import { Language } from "utils/language";

import { TranslateFooterHTML, TranslationKey } from "../../i18n";
import en from "../../i18n/en.json";
import { FooterHref, FooterNodeType } from "../../model";

import { expansionNodeStyles as styles } from "./ExpansionNode.styles";

const createListItemButtonProps = ({
  clickOneTrustButton,
  href,
  newTab,
  pageLanguage,
  setFeedbackFormOpen,
  titleKey,
}: {
  clickOneTrustButton: VoidFunction;
  href: FooterHref;
  newTab?: boolean;
  pageLanguage: Language;
  setFeedbackFormOpen: (open: boolean) => void;
  titleKey: TranslationKey;
}) => {
  if (titleKey === "feedback" || titleKey === "cookieSettings") {
    return {
      component: "button",
      onClick:
        titleKey === "feedback"
          ? () => setFeedbackFormOpen(true)
          : clickOneTrustButton,
    };
  }
  return {
    component: "a",
    href: href[pageLanguage] || href.en,
    ...(newTab ? { target: "_blank" } : {}),
  };
};

export interface ExpansionLinkGroupNodeProps {
  clickOneTrustButton: VoidFunction;
  isAuthenticated?: boolean;
  node: FooterNodeType.Parent;
  pageLanguage: Language;
  setFeedbackFormOpen: (open: boolean) => void;
  translate: (key: TranslationKey) => string;
}

export const ExpansionNode: FC<ExpansionLinkGroupNodeProps> = ({
  clickOneTrustButton,
  isAuthenticated,
  node,
  pageLanguage,
  setFeedbackFormOpen,
  translate,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <ListItemButton
        divider
        onClick={() => setOpen((open) => !open)}
        css={styles.expansionListItem}
      >
        <ListItemText>
          <Typography variant="h5" data-testid={node.titleKey}>
            {translate(node.titleKey)}
          </Typography>
        </ListItemText>
        <ParentNodeIcon open={open} closedDirection="down" color="white" />
      </ListItemButton>
      <Collapse open={open}>
        <List>
          {node.childNodes.map((childNode) => {
            if (childNode.titleKey === "feedback" && !isAuthenticated) {
              return null;
            }

            const linkAnalyticsProp =
              node.titleKey === "moodysIntegrityHotline"
                ? {}
                : generateFooterAnalyticsProp({
                    moduleName: en.translation[node.titleKey],
                    name: en.translation[childNode.titleKey],
                  });
            const listItemButtonProps = createListItemButtonProps({
              clickOneTrustButton,
              href: childNode.href,
              pageLanguage,
              setFeedbackFormOpen,
              titleKey: childNode.titleKey,
              newTab: childNode.newTab,
            });
            return (
              <ListItemButton
                key={childNode.titleKey}
                disableGutters
                css={styles.link}
                {...listItemButtonProps}
                {...linkAnalyticsProp}
                data-testid={childNode.titleKey}
              >
                <ListItemText>
                  {childNode.superTitleKey ? (
                    <>
                      {translate(childNode.superTitleKey)}
                      <br />
                    </>
                  ) : null}
                  <TranslateFooterHTML keyName={childNode.titleKey} />
                </ListItemText>
              </ListItemButton>
            );
          })}
        </List>
      </Collapse>
      <Divider />
    </>
  );
};
